import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concatMap, filter, forkJoin, fromEvent, map, Observable, range, retry, switchMap, take, tap, throwError, timer, toArray } from 'rxjs';
import { ModuleType } from 'src/app/interfaces/shared/module';
import { environment } from 'src/environments/environment';
import { Attachment, AttachmentSource, LastAttachment, LastAttachmentByDatabase, PostAttachment, PutAttachment } from '../../../interfaces/api/attachment';
import { Video, VideoRecordingStart, VideoRecordingStatus } from '../../../interfaces/api/video';
import { ApiService } from '../../core/api.service';

const VIDEO_CHUNK_BYTES = 16 * 1024 * 1024;
const VIDEO_CHUNK_RETRIES = 5;

type QueryParams =Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root'
})

export class AttachmentService {

  collection = "Attachment";
  path = "attachments";
  attachments: Attachment[] = [];

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) { }

  //---GET---//

  getAttachments(query: QueryParams): Observable<Attachment[]> {
    return this.apiService.postCollectionData(this.path, 'getAttachments', query)
    // .pipe(
    //   tap((res: any) => {
    //     try {
    //       if (res)
    //         localStorage.setItem('attachments', res);
    //     } catch (e) {
    //       console.warn(e)
    //     }
    //   }), catchError(err => throwError(() => err))
    // );
  }

  getAttachment(id: string): Observable<Attachment> {
    return this.apiService.getCollectionItem(this.path, "", id)
  }

  getAttachmentImage(url: string): Observable<string> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      switchMap(blob => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        return fromEvent(reader, 'loadend').pipe(
          take(1),
          map(() => reader.result as string)
        );
      })
    );
  }

  getAttachmentsByMetricsGrouped(query: QueryParams): Observable<{
    attachments: Attachment[];
    offset: number;
    limit: number;
    hasMore: boolean;
    total: number;
    processed: number;
  }> {
    return this.apiService.postCollectionData(this.path, 'getAttachmentsByMetricsGrouped', query);
  }

  searchAttachments(search: string, query: QueryParams = {}): Observable<Attachment[]> {
    query['q'] = search;
    return this.apiService.postCollectionData(this.path, 'search', query)
  }

  getLastAttachments(database: string = ""): Observable<LastAttachment[]> {
    return this.apiService.getCollection(this.path, 'last-attachment', { database });
  }

  getLastAttachmentsByDatabase(databases: string[]): Observable<LastAttachmentByDatabase[]> {
    return forkJoin(
      databases.map(database => this.getLastAttachments(database)
        .pipe(map(attachments => ({ database, attachments })))
      )
    )
  }

  exportAttachments(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.path, 'export', query)
  }

  getAttachmentsCount(query: QueryParams = {}): Observable<number> {
    return this.apiService.getCollection(this.path, 'count', query)
  }

  getTags(project?: string): Observable<any> {
    return this.apiService.getCollection(this.path, 'tags', { project });
  }

  getTagStats(): Observable<any> {
    return this.apiService.getCollection(this.path, 'tagStats');
  }

  getValueList(field: string): Observable<any> {
    return this.apiService.getCollection(this.path, 'listValues', { field });
  }

  //---POST---//

  postWithPredict(attachment: PostAttachment): Observable<Attachment> {
    return this.apiService.postCollectionData(this.path, "predict", { ...attachment });
  }

  postManyAttachments(attachments: PostAttachment[]): Observable<{
    total: number;
    createdCount: number;
    errorCount: number;
    created: Attachment[];
    errors: any[];
  }> {
    return this.apiService.postCollectionData(this.path, 'many/predict', attachments);
  }

  completeUploadBatch(batch: { batchId: string; total: number; source: AttachmentSource; event?: string }): Observable<{
    batchId: string;
    total: number;
    uploaded: number;
  }> {
    return this.apiService.postCollectionData(this.path, 'many/complete', batch);
  }

  postVideoAttachment(params: {
    file: File;
    thumbnail: string;
    tracker: string;
    trackerName?: string;
    event?: string;
    camera?: string;
    createdDate: string;
    source?: AttachmentSource;
    uploadId?: string;
    onProgress?: (percent: number) => void;
  }): Observable<{ attachment: Attachment; video: Video }> {
    const { file } = params;
    const uploadId = params.uploadId ?? crypto.randomUUID();
    const chunkCount = Math.max(1, Math.ceil(file.size / VIDEO_CHUNK_BYTES));
    const chunkParams: Record<string, string> = { uploadId, tracker: params.tracker, createdDate: params.createdDate };
    if (params.trackerName) chunkParams['trackerName'] = params.trackerName;

    const sendChunk = (index: number) => {
      const start = index * VIDEO_CHUNK_BYTES;
      const chunk = file.slice(start, Math.min(file.size, start + VIDEO_CHUNK_BYTES));
      return this.http.post(`${environment.API_URL}/${this.path}/video/chunk`, chunk, {
        params: { ...chunkParams, index, 'ngsw-bypass': true },
        headers: { 'Content-Type': 'application/octet-stream' },
        reportProgress: true,
        observe: 'events',
      }).pipe(
        tap(event => {
          if (!file.size) return;
          if (event.type === HttpEventType.UploadProgress)
            params.onProgress?.(Math.round((start + event.loaded) / file.size * 100));
          else if (event.type === HttpEventType.Response)
            params.onProgress?.(Math.round((start + chunk.size) / file.size * 100));
        }),
        filter(event => event.type === HttpEventType.Response),
        retry({
          count: VIDEO_CHUNK_RETRIES,
          delay: (error: HttpErrorResponse, attempt) =>
            error.status === 0 || error.status === 429 || error.status >= 500
              ? timer(attempt * 2000)
              : throwError(() => error),
        }),
      );
    };

    return range(0, chunkCount).pipe(
      concatMap(sendChunk),
      toArray(),
      switchMap(() => this.http.post<{ attachment: Attachment; video: Video }>(
        `${environment.API_URL}/${this.path}/video/complete?ngsw-bypass=true`,
        {
          uploadId,
          chunkCount,
          path: params.thumbnail,
          tracker: params.tracker,
          trackerName: params.trackerName,
          event: params.event,
          camera: params.camera,
          createdDate: params.createdDate,
          source: params.source,
          size: file.size,
        }
      )),
    );
  }

  startVideoRecording(body: VideoRecordingStart, db?: string): Observable<VideoRecordingStatus> {
    return this.http.post<VideoRecordingStatus>(
      `${environment.API_URL}/${this.path}/video/recording/start?ngsw-bypass=true`,
      body,
      { headers: this.dbHeaders(db) },
    );
  }

  postVideoRecordingChunk(uploadId: string, index: number, chunk: Blob, db?: string): Observable<unknown> {
    return this.http.post(`${environment.API_URL}/${this.path}/video/chunk`, chunk, {
      params: { uploadId, index, 'ngsw-bypass': true },
      headers: { ...this.dbHeaders(db), 'Content-Type': 'application/octet-stream' },
    });
  }

  completeVideoRecording(body: { uploadId: string; chunkCount: number; path?: string }, db?: string): Observable<{ attachment: Attachment; video: Video }> {
    return this.http.post<{ attachment: Attachment; video: Video }>(
      `${environment.API_URL}/${this.path}/video/complete?ngsw-bypass=true`,
      body,
      { headers: this.dbHeaders(db) },
    );
  }

  getVideoRecording(uploadId: string, db?: string): Observable<VideoRecordingStatus> {
    return this.http.get<VideoRecordingStatus>(
      `${environment.API_URL}/${this.path}/video/recording/${uploadId}?ngsw-bypass=true`,
      { headers: this.dbHeaders(db) },
    );
  }

  private dbHeaders(db?: string): Record<string, string> {
    return db ? { 'X-Project-Db': db } : {};
  }

  classify(attachment: PostAttachment | {} = {}, tag: string | null, project: ModuleType): Observable<any> {
    return this.apiService.postCollectionData(this.path, 'classify', { attachment, tag, project });
  }

  training(attachment: PostAttachment | {} = {}, regions: any[] = [], project: string = "crane", unpixelate: boolean = false, otherRegions: any[] = []): Observable<any> {
    return this.apiService.postCollectionData(this.path, 'training', { attachment, regions, project, unpixelate, otherRegions });
  }

  //---PUT---//

  setAttachment(attachment: PutAttachment): Observable<any> {
    return this.apiService.updateCollection(this.path, '', attachment);
  }

  updateManyAttachment(attachments: PutAttachment[] = [], project: string = "crane") {
    return this.apiService.updateCollection(this.path, 'many', { attachments, project });
  }

  setAttachmentLabel(attachment: PutAttachment): Observable<any> {
    return this.apiService.updateCollection(this.path, 'label', attachment);
  }

  //---DELETE---//

}
